import React, { useEffect, useState } from 'react';
import axios from 'axios';
import io from 'socket.io-client';
import url from '../url.js';



const MessageThread = ({ activityId, selectedGroupId }) => {
    const [data, setData] = useState({ nodes: [], edges: [] });
    const [expandedNodes, setExpandedNodes] = useState([]);
    const [ws, setSocket] = useState(null);
    const [tree, setTree] = useState([]);

    useEffect(() => {
        setSocket(io.connect(url.socketioHost,{path: '/s/socket.io'}));
    }, []);

    useEffect(() => {
        if (ws) {
            ws.on('connect', () => {
                
            });

            // ✅ 使用傳入的 activityId
            if (!activityId || activityId === 'null') {
                console.error('[MessageThread] activityId 無效，無法訂閱 WebSocket 事件');
                return;
            }
            
            const nodeReceiveEvent = `node-recieve-${activityId}`;
            const edgeReceiveEvent = `edge-recieve-${activityId}`;

            //

            ws.on(nodeReceiveEvent, handleNodeReceive);
            ws.on(edgeReceiveEvent, handleEdgeReceive);

            ws.on('error', (error) => {
                console.error('WebSocket error:', error);
            });

            ws.on('disconnect', () => {
                
            });

            return () => {
                ws.off(nodeReceiveEvent, handleNodeReceive);
                ws.off(edgeReceiveEvent, handleEdgeReceive);
            };
        }
    }, [ws, activityId]);

    useEffect(() => {
        if (activityId) {
            setData({ nodes: [], edges: [] });
            fetchNodesAndEdges();
        }
    }, [activityId, selectedGroupId]);

    const fetchNodesAndEdges = async () => {
        try {
            if (!activityId) {
                console.error('[MessageThread] 缺少 activityId');
                return;
            }

            console.log('[MessageThread] 開始獲取數據，activityId:', activityId, 'selectedGroupId:', selectedGroupId);

            const jwtToken = localStorage.getItem('jwtToken');
            const headers = jwtToken ? { Authorization: `Bearer ${jwtToken}` } : {};

            // 先獲取該活動下所有小組
            const groupsResponse = await axios.get(
                `${url.backendHost}api/activities/${activityId}`,
                { headers, withCredentials: true }
            );

            const groups = groupsResponse.data.Groups || [];
            console.log('[MessageThread] 獲取到小組數:', groups.length);

            let allNodes = [];
            let allEdges = [];

            // 遍歷所有小組（或特定小組），獲取節點和邊
            const targetGroups = selectedGroupId 
                ? groups.filter(g => g.id == selectedGroupId)
                : groups;

            console.log('[MessageThread] 需要獲取的小組數:', targetGroups.length);

            for (const group of targetGroups) {
                try {
                    const [nodeResponse, edgeResponse] = await Promise.all([
                        axios.get(`${url.backendHost}api/nodes/all/${group.id}`, { headers, withCredentials: true }),
                        axios.get(`${url.backendHost}api/edges/all/${group.id}`, { headers, withCredentials: true })
                    ]);

                    const groupNodes = nodeResponse.data[0]?.Nodes || [];
                    const groupEdges = edgeResponse.data || [];

                    console.log(`[MessageThread] 小組 ${group.groupName} - 節點數: ${groupNodes.length}, 邊數: ${groupEdges.length}`);

                    allNodes = allNodes.concat(groupNodes);
                    allEdges = allEdges.concat(groupEdges);
                } catch (err) {
                    console.error(`[MessageThread] 獲取小組 ${group.id} 數據失敗:`, err);
                }
            }

            console.log('[MessageThread] 總節點數:', allNodes.length, '總邊數:', allEdges.length);

            const nodes = allNodes.map(node => ({
                ...node,
                id: String(node.id)
            }));
            const edges = allEdges.map(edge => ({
                ...edge,
                from: String(edge.from),
                to: String(edge.to)
            }));
            
            console.log('[MessageThread] 設置數據 - 節點數:', nodes.length, '邊數:', edges.length);
            setData({ nodes, edges });
        } catch (error) {
            console.error('[MessageThread] Error fetching nodes and edges:', error.message);
            console.error('[MessageThread] Error details:', error.response?.data || error);
        }
    };

    useEffect(() => {
        const { nodes, edges } = data;
        //
        const tree = buildTree(nodes, edges);
        //
        setTree(tree);
    }, [data]);

    const handleNodeReceive = (body) => {
        // 如果選擇了特定小組，只顯示該小組的節點；否則顯示所有小組
        if (!selectedGroupId || body.groupId == selectedGroupId) {
            const normalizedNode = {
                ...body,
                id: String(body.id),
                groupId: String(body.groupId),
                activityId: body.activityId ? String(body.activityId) : undefined,
                createdAt: body.createdAt || new Date().toISOString(),
                updatedAt: body.updatedAt || new Date().toISOString(),
            };
            setData(prevData => {
                const updatedNodes = [...prevData.nodes, normalizedNode];
                return { ...prevData, nodes: updatedNodes };
            });
        }
    };

    const handleEdgeReceive = (body) => {
        // 如果選擇了特定小組，只顯示該小組的邊；否則顯示所有小組
        if (!selectedGroupId || body.groupId == selectedGroupId) {
            const normalizedEdge = {
                ...body,
                groupId: String(body.groupId),
                from: String(body.from),
                to: String(body.to),
                activityId: body.activityId ? String(body.activityId) : undefined,
                createdAt: body.createdAt || new Date().toISOString(),
                updatedAt: body.updatedAt || new Date().toISOString(),
            };
            setData(prevData => {
                const updatedEdges = [...prevData.edges, normalizedEdge];
                return { ...prevData, edges: updatedEdges };
            });
        }
    };

    const buildTree = (nodes, edges) => {
        if (!Array.isArray(nodes) || !Array.isArray(edges)) return [];

        const nodeMap = new Map(nodes.map(node => [node.id, { ...node, children: [] }]));

        edges.forEach(edge => {
            const parent = nodeMap.get(edge.to);
            const child = nodeMap.get(edge.from);
            if (parent && child) {
                child.level = (parent.level || 0) + 1;
                parent.children.push(child);
            }
        });

        const tree = [];
        nodeMap.forEach(node => {
            if (!edges.some(edge => edge.from === node.id)) {
                node.level = 0;
                tree.push(node);
            }
        });

        return tree;
    };

    const toggleExpand = (nodeId) => {
        if (expandedNodes.includes(nodeId)) {
            setExpandedNodes(expandedNodes.filter(id => id !== nodeId));
        } else {
            setExpandedNodes([...expandedNodes, nodeId]);
        }
    };

    const renderTree = (nodes) => {
        return nodes.map(node => (
            <div key={node.id} style={getStyle(node.level)}>
                <div style={headerStyle}>
                    <div style={authorStyle}>{node.title} - {node.author}</div>
                    <button onClick={() => toggleExpand(node.id)} style={buttonStyle}>
                        {expandedNodes.includes(node.id) ? '收起回覆' : '展開回覆'}
                    </button>
                </div>
                {expandedNodes.includes(node.id) && <div style={contentStyle}>{node.content}</div>}
                {expandedNodes.includes(node.id) && node.children && <div>{renderTree(node.children)}</div>}
            </div>
        ));
    };

    const getStyle = (level) => ({
        marginLeft: '0', 
        borderLeft: level > 0 ? '2px solid #ddd' : 'none', 
        padding: '10px',
        marginBottom: '10px',
        background: '#fff',
        borderRadius: '5px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
        fontSize: '14px',
        position: 'relative'
    });

    const headerStyle = {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '5px'
    };

    const authorStyle = {
        fontWeight: 'bold', 
        color: '#333'
    };

    const buttonStyle = {
        background: 'none',
        border: 'none',
        color: '#97CBFF', 
        cursor: 'pointer',
        fontSize: '12px'
    };

    const contentStyle = {
        marginTop: '10px',
        backgroundColor: '#f9f9f9',
        borderRadius: '5px',
        padding: '10px',
        fontSize: '14px',
        color: '#666'
    };
    const containerStyle = {
        width: '90%',
        height: '85%',
        overflow: 'auto',
        fontFamily: 'Arial, sans-serif',
        border: '1px solid #ddd',
        borderRadius: '5px',
        padding: '10px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
        position: 'relative'
    };
    return (
        <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={containerStyle}>
                {tree.length === 0 ? (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        color: '#999',
                        padding: '40px'
                    }}>
                        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginBottom: '16px' }}>
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                        </svg>
                        <p style={{ fontSize: '16px', fontWeight: '500', marginBottom: '8px', color: '#666' }}>
                            目前沒有討論內容
                        </p>
                        <p style={{ fontSize: '14px', color: '#999', textAlign: 'center' }}>
                            {selectedGroupId ? '此小組尚未有任何討論節點' : '選擇小組後即可查看討論串'}
                        </p>
                    </div>
                ) : (
                    <div>
                        <h2 style={{ textAlign: 'center', fontSize: '22px', color: '#333' }}>想法串</h2>
                        {renderTree(tree)}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MessageThread;
